import {
	BaseAPIResponse,
	CompleteDrivePaymentAPIInput,
	CreateDrivePaymentAPIResponse,
	GetDriveForJobseekerAPIResponse,
	GetMyDriveStatusAPIResponse,
	GetUpcomingDrivesForJobseekerAPIResponse,
	CreateDriveRegistrationApplicationInput,
	GetAllDrivesAPIResponse,
	GetDriveCandidatesAPIResponse,
	GetDriveCompaniesAPIInput,
	GetDriveCompaniesAPIResponse,
	GetDrivePositionApplicantsAPIResponse,
	GetDrivePositionsAPIInput,
	GetDrivePositionsAPIResponse,
	GetDriveRegistrationApplicationResponse,
	PositionStatus,
	RecruiterFormData,
	RegisterForDriveAPIInput,
	UserAvailabilityInput,
	UserFiltersForApis,
} from 'types';
import { baseApi } from './axios';
import { API_ENDPOINT } from 'src/config/env.config';

const baseUrl = `${API_ENDPOINT}/drive`;

export const getAllDrives = async (data?: {
	showInternalDrives?: boolean;
}): Promise<GetAllDrivesAPIResponse> => {
	const response = await baseApi.get(`${baseUrl}/all`, { params: data });
	return response.data;
};

export const registerForDrive = async (data: RegisterForDriveAPIInput): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/register`, data);
	return response.data;
};

export const updateDrivePosition = async (id: string, data: RecruiterFormData): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/updateDrivePosition/${id}`, data);
	return response.data;
};

export const getDriveCompanies = async (data?: GetDriveCompaniesAPIInput): Promise<GetDriveCompaniesAPIResponse> => {
	const response = await baseApi.get(`${baseUrl}/getDriveCompanies`, { params: data });
	return response.data;
};

export const getDrivePositions = async (data?: GetDrivePositionsAPIInput): Promise<GetDrivePositionsAPIResponse> => {
	const response = await baseApi.get(`${baseUrl}/getDrivePositions`, { params: data });
	return response.data;
};

export const getDriveCandidates = async (
	data?: UserFiltersForApis & { page: number; limit?: number; position?: string },
): Promise<GetDriveCandidatesAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/getDriveCandidates`, data);
	return response.data;
};

export const getDrivePositionApplicants = async (
	data?: UserFiltersForApis & { page: number; limit?: number; position?: string },
): Promise<GetDrivePositionApplicantsAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/getDrivePositionApplicants`, data);
	return response.data;
};

export const shortlistDriveCandidate = async (data: {
	userId: string;
	positionId: string;
}): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/shortlistCandidate`, data);
	return response.data;
};

export const closeDrivePosition = async (data: { positionId: string; reason: string }): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/closeDrivePosition/${data.positionId}`, {
		reason: data?.reason,
		status: PositionStatus.CLOSED,
	});
	return response.data;
};

export const createDriveRegistrationApplication = async (
	driveApplication: CreateDriveRegistrationApplicationInput,
): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/create-drive-registration-application`, driveApplication);
	return response.data;
};

export const getDriveRegistrationApplication = async (): Promise<GetDriveRegistrationApplicationResponse> => {
	const response = await baseApi.get(`${baseUrl}/get-drive-registration-application-status`);
	return response.data;
};

export const startDriveRegistrationAssessment = async (data: {
	driveApplicationId: string;
	isMock: boolean;
}): Promise<BaseAPIResponse & { url: string }> => {
	const response = await baseApi.post(`${baseUrl}/start-drive-registration-assessment`, data);
	return response.data;
};

export const driveRegistrationScheduleCounselling = async (data: {
	driveApplicationId: string;
	selectedCounsellingSlot: UserAvailabilityInput;
}): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/drive-registration-schedule-counselling`, data);
	return response.data;
};

// jobseeker placement drive application flow
export const getUpcomingDrivesForJobseeker = async (): Promise<GetUpcomingDrivesForJobseekerAPIResponse> => {
	const response = await baseApi.get(`${baseUrl}/jobseeker/upcoming`);
	return response.data;
};

export const getDriveForJobseeker = async (driveId: string): Promise<GetDriveForJobseekerAPIResponse> => {
	const response = await baseApi.get(`${baseUrl}/jobseeker/${driveId}`);
	return response.data;
};

// Public (logged out) drive browsing and guest onboarding, for the QR code landing flow.
export const getPublicUpcomingDrives = async (): Promise<GetUpcomingDrivesForJobseekerAPIResponse> => {
	const response = await baseApi.get(`${baseUrl}/public/upcoming`);
	return response.data;
};

export const getPublicDrive = async (driveId: string): Promise<GetDriveForJobseekerAPIResponse> => {
	const response = await baseApi.get(`${baseUrl}/public/${driveId}`);
	return response.data;
};

export const guestDriveSendOtp = async (payload: {
	email: string;
	name?: string;
	phone?: string;
	driveId: string;
	mode: 'register' | 'login';
}): Promise<{ error: boolean; message: string }> => {
	const response = await baseApi.post(`${baseUrl}/public/guest/send-otp`, payload);
	return response.data;
};

export const guestDriveVerifyOtp = async (payload: {
	email: string;
	otp: string;
	driveId: string;
	mode: 'register' | 'login';
}): Promise<{ error: boolean; token: string }> => {
	const response = await baseApi.post(`${baseUrl}/public/guest/verify-otp`, payload);
	return response.data;
};

export const getMyDriveStatus = async (driveId: string): Promise<GetMyDriveStatusAPIResponse> => {
	const response = await baseApi.get(`${baseUrl}/jobseeker/${driveId}/status`);
	return response.data;
};

export const applyToDrivePositions = async (data: { driveId: string; positionIds: string[] }): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/jobseeker/apply`, data);
	return response.data;
};

export interface ValidateDriveCouponResponse extends BaseAPIResponse {
	code: string;
	discountPercentage: number;
	discountAmount: number;
	finalAmount: number;
}

export const validateDriveCoupon = async (data: { driveId: string; couponCode: string }): Promise<ValidateDriveCouponResponse> => {
	const response = await baseApi.post(`${baseUrl}/jobseeker/validate-coupon`, data);
	return response.data;
};

export const createDrivePayment = async (data: { driveId: string; couponCode?: string }): Promise<CreateDrivePaymentAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/jobseeker/create-payment`, data);
	return response.data;
};

export const completeDrivePayment = async (data: CompleteDrivePaymentAPIInput): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/jobseeker/complete-payment`, data);
	return response.data;
};

export const cancelDrivePayment = async (data: { paymentId: string }): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/jobseeker/cancel-payment`, data);
	return response.data;
};

export const rejectDriveCandidate = async (data: { userId: string; positionId: string; remarks?: string }): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/rejectCandidate`, data);
	return response.data;
};

export interface InvitePosition {
	_id: string;
	title: string;
	positionLabel?: string;
	registeredDrive: string;
	company?: { _id: string; name: string; logo?: string };
}

export const getPositionsForInvite = async (driveIds: string[]): Promise<{ positions: InvitePosition[] }> => {
	const response = await baseApi.get(`${baseUrl}/positionsForInvite`, { params: { driveIds: driveIds.join(',') } });
	return response.data;
};
